import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import { Input, Button, DatePicker, Typography, Spin, notification, Modal } from 'antd';
import { RootState, AppDispatch } from '../../../redux/store/store';
import { createPaciente, updatePaciente, fetchPacientes } from '../../../redux/actions/pacientes/Pacientes';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import { PacienteSchema } from '../../../types/pacientes/Pacientes'

const { Title } = Typography;

const CrearPacientes: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { loading, success, error, pacientes } = useSelector((state: RootState) => state.pacientes);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [datePickerValue, setDatePickerValue] = useState<any>(null);
  const [initialValues, setInitialValues] = useState({
    nombre: '',
    apellidos: '',
    cedula: '',
    email: '',
    genero: '',
    lugar_nacimiento: '',
    direccion: '',
    celular: '',
    edad: '',
    fecha_nacimiento: '',
    medico_cabecera: '',
    contacto_emergencia_nombre: '',
    contacto_emergencia_celular: '',
    contacto_emergencia_parentesco: '',
  });
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (id) {
      setIsEditMode(true);
      if (pacientes.length === 0) {
        dispatch(fetchPacientes());
      }
    } else {
      setIsEditMode(false);
      setInitialValues({
        nombre: '',
        apellidos: '',
        cedula: '',
        email: '',
        genero: '',
        lugar_nacimiento: '',
        direccion: '',
        celular: '',
        edad: '',
        fecha_nacimiento: '',
        medico_cabecera: '',
        contacto_emergencia_nombre: '',
        contacto_emergencia_celular: '',
        contacto_emergencia_parentesco: '',
      });
      setDatePickerValue(null);
    }
  }, [id, pacientes.length, dispatch]);

  useEffect(() => {
    if (isEditMode && pacientes.length > 0 && id) {
      const paciente = pacientes.find(p => p.id === parseInt(id, 10));

      if (paciente) {
        setInitialValues({
          nombre: paciente.nombre || '',
          apellidos: paciente.apellidos || '',
          cedula: paciente.cedula || '',
          email: paciente.email || '',
          genero: paciente.genero || '',
          lugar_nacimiento: paciente.lugar_nacimiento || '',
          direccion: paciente.direccion || '',
          celular: paciente.celular || '',
          edad: paciente.edad || '',
          fecha_nacimiento: paciente.fecha_nacimiento || '',
          medico_cabecera: paciente.medico_cabecera || '',
          contacto_emergencia_nombre: paciente.contacto_emergencia_nombre || '',
          contacto_emergencia_celular: paciente.contacto_emergencia_celular || '',
          contacto_emergencia_parentesco: paciente.contacto_emergencia_parentesco || '',
        });

        if (paciente.fecha_nacimiento) {
          setDatePickerValue(moment(paciente.fecha_nacimiento));
        }
      } else {
        notification.warning({
          message: 'Atención',
          description: 'Cargando datos del paciente...',
          placement: 'top'
        });
        dispatch(fetchPacientes());
      }
    }
  }, [isEditMode, pacientes, id, dispatch]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (success) {
      timer = setTimeout(() => {
      }, 3000);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [success]);

  const handleSubmit = async (values: any, { resetForm }: any) => {
    try {
      if (isEditMode && id) {
        try {
          await dispatch(updatePaciente(id, values));

          notification.success({
            message: 'Paciente actualizado',
            description: 'El paciente ha sido actualizado exitosamente.',
            placement: 'top',
            duration: 3
          });
        } catch (updateErr: any) {
          notification.destroy();
          const updateErrorMessage = updateErr?.message || (typeof updateErr === 'string' ? updateErr : '');
          console.log('Error message:', updateErrorMessage);
          if (
            updateErrorMessage.includes('Ya existe un paciente con esta cédula') ||
            updateErrorMessage.includes('Ya existe otro paciente con esta cédula') ||
            error?.includes('Ya existe un paciente con esta cédula') ||
            error?.includes('Ya existe otro paciente con esta cédula')
          ) {
            setErrorMessage('Ya existe un paciente con esta cédula');
            setErrorModalVisible(true);
          } else {
            setErrorMessage('Hubo un problema al actualizar el paciente.');
            setErrorModalVisible(true);
          }
          throw updateErr;
        }
      } else {
        await dispatch(createPaciente(values));

        notification.success({
          message: 'Paciente creado',
          description: 'El paciente ha sido creado exitosamente.',
          placement: 'top',
          duration: 3
        });
        resetForm();
        setDatePickerValue(null);
      }
    } catch (err: any) {
      const errorMessage = err?.message || (typeof err === 'string' ? err : '');
      if (errorMessage.includes('Ya existe un paciente con esta cédula') || error?.includes('Ya existe un paciente con esta cédula')) {
        setErrorMessage('Ya existe un paciente con esta cédula');
        setErrorModalVisible(true);
      } else {
        notification.error({
          message: 'Error',
          description: isEditMode
            ? 'Hubo un problema al actualizar el paciente.'
            : 'Hubo un problema al crear el paciente.',
          placement: 'top'
        });
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <Title level={2} className="mb-6 text-center">
        {isEditMode ? 'Editar Paciente' : 'Registro de Paciente'}
      </Title>
      <Modal
        title="Error de Registro"
        open={errorModalVisible}
        onOk={() => setErrorModalVisible(false)}
        onCancel={() => setErrorModalVisible(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setErrorModalVisible(false)}>
            Entendido
          </Button>
        ]}
      >
        <p>{errorMessage}</p>
      </Modal>

      <Formik
        initialValues={initialValues}
        validationSchema={PacienteSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({ errors, touched, setFieldValue, values }) => (
          <Form className="space-y-6 p-6 bg-white shadow-lg rounded-lg">
            {loading && <div className="flex justify-center mb-4"><Spin size="large" /></div>}

            <Title level={3}>Información Personal</Title>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Field
                  name="nombre"
                  as={Input}
                  placeholder="Nombres"
                  className={errors.nombre && touched.nombre ? 'border-red-500' : ''}
                />
                <ErrorMessage name="nombre" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <Field
                  name="apellidos"
                  as={Input}
                  placeholder="Apellidos"
                  className={errors.apellidos && touched.apellidos ? 'border-red-500' : ''}
                />
                <ErrorMessage name="apellidos" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <Field
                  name="email"
                  as={Input}
                  type="email"
                  placeholder="Email"
                  className={errors.email && touched.email ? 'border-red-500' : ''}
                />
                <ErrorMessage name="email" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Field
                  name="cedula"
                  as={Input}
                  placeholder="Nro. Cédula"
                  className={errors.cedula && touched.cedula ? 'border-red-500' : ''}
                />
                <ErrorMessage name="cedula" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <Field
                  name="edad"
                  as={Input}
                  placeholder="Edad"
                  className={errors.edad && touched.edad ? 'border-red-500' : ''}
                />
                <ErrorMessage name="edad" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <DatePicker
                  className={`w-full ${errors.fecha_nacimiento && touched.fecha_nacimiento ? 'border-red-500' : ''}`}
                  placeholder="Fecha de Nacimiento"
                  value={datePickerValue}
                  onChange={(date) => {
                    setDatePickerValue(date);
                    const formattedDate = date ? date.format('YYYY-MM-DD') : '';
                    setFieldValue('fecha_nacimiento', formattedDate);
                  }}
                />
                <ErrorMessage name="fecha_nacimiento" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Field
                  name="genero"
                  as="select"
                  className={`w-full h-8 border rounded ${errors.genero && touched.genero ? 'border-red-500' : 'border-gray-300'}`}
                >
                  <option value="">Seleccione género</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Femenino">Femenino</option>
                  <option value="Otro">Otro</option>
                </Field>
                <ErrorMessage name="genero" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <Field
                  name="lugar_nacimiento"
                  as={Input}
                  placeholder="Lugar de Nacimiento"
                  className={errors.lugar_nacimiento && touched.lugar_nacimiento ? 'border-red-500' : ''}
                />
                <ErrorMessage name="lugar_nacimiento" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            </div>

            <div>
              <Field
                name="direccion"
                as={Input}
                placeholder="Dirección Residencial"
                className={errors.direccion && touched.direccion ? 'border-red-500' : ''}
              />
              <ErrorMessage name="direccion" component="div" className="text-red-500 text-sm mt-1" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Field
                  name="celular"
                  as={Input}
                  placeholder="Celular"
                  className={errors.celular && touched.celular ? 'border-red-500' : ''}
                />
                <ErrorMessage name="celular" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <Field
                  name="medico_cabecera"
                  as={Input}
                  placeholder="Médico de Cabecera"
                  className={errors.medico_cabecera && touched.medico_cabecera ? 'border-red-500' : ''}
                />
                <ErrorMessage name="medico_cabecera" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            </div>

            <Title level={3}>En Caso de Emergencia</Title>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Field
                  name="contacto_emergencia_nombre"
                  as={Input}
                  placeholder="Nombre del Contacto"
                  className={errors.contacto_emergencia_nombre && touched.contacto_emergencia_nombre ? 'border-red-500' : ''}
                />
                <ErrorMessage name="contacto_emergencia_nombre" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <Field
                  name="contacto_emergencia_parentesco"
                  as={Input}
                  placeholder="Parentesco"
                  className={errors.contacto_emergencia_parentesco && touched.contacto_emergencia_parentesco ? 'border-red-500' : ''}
                />
                <ErrorMessage name="contacto_emergencia_parentesco" component="div" className="text-red-500 text-sm mt-1" />
              </div>

              <div>
                <Field
                  name="contacto_emergencia_celular"
                  as={Input}
                  placeholder="Nro. Celular"
                  className={errors.contacto_emergencia_celular && touched.contacto_emergencia_celular ? 'border-red-500' : ''}
                />
                <ErrorMessage name="contacto_emergencia_celular" component="div" className="text-red-500 text-sm mt-1" />
              </div>
            </div>

            <div className={`flex ${isEditMode ? 'justify-between' : 'justify-end'} mt-6`}>
              {isEditMode && (
                <Button
                  onClick={() => navigate('/pacientes/lista')}
                  className="w-1/4"
                >
                  Cancelar
                </Button>
              )}

              <Button
                type="primary"
                htmlType="submit"
                className={isEditMode ? "w-3/4 ml-4" : "w-full"}
                loading={loading}
                disabled={loading}
              >
                {isEditMode ? 'Actualizar Paciente' : 'Crear Paciente'}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default CrearPacientes;